import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "./ui";
import { api } from "../lib/api";
import { useTranslation } from "react-i18next";

/**
 * The warning shown before a member spends their one conversation.
 *
 * It is deliberately a stop, not a hint. This is the only thing on the platform that a
 * member gets once a month and cannot undo — the investor either answers or does not,
 * and either way the choice is made. Somebody who clicks it by accident and discovers
 * the cost afterwards is a support ticket we wrote ourselves.
 *
 * Every fact in the warning comes from `/messages/eligibility` rather than from copy,
 * so the window length, the request expiry and whether the slot is even free are the
 * server's answer and not the frontend's guess.
 */
export default function MessageRequestDialog({ open, onClose, investorId, investorName, onSent }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBody(""); setConfirmed(false); setState(null);
    api.dmEligibility().then((r) => setState(r.data)).catch(() => {});
  }, [open]);

  const send = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.dmRequest(investorId, body.trim());
      toast.success(t("dm.sent"));
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
    } finally { setBusy(false); }
  };

  const locked = state && !state.has_full_access;
  const busySlot = state && state.has_full_access && !state.slot_free;
  const canSend = state?.has_full_access && state.slot_free && confirmed
    && body.trim().length >= 10 && !busy;

  return (
    <Modal open={open} onClose={onClose} title={t("dm.startTitle")} testid="dm-request-dialog">
      {!state ? (
        <div className="py-8 text-center"><Loader2 className="animate-spin text-brand-500 mx-auto" size={22} /></div>
      ) : locked ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">{t("dm.needsFull")}</p>
          <Link to="/billing" className="btn-primary w-full justify-center" data-testid="dm-upgrade">
            {t("dm.seePlans")}
          </Link>
        </div>
      ) : busySlot ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            {t("dm.alreadyOpen", { name: state.grant?.counterparty_name })}
          </p>
          <Link to="/messages" className="btn-outline w-full justify-center" data-testid="dm-goto">
            {t("dm.openConversation")}
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {/* The cost, stated before the field they'd type into. */}
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 flex gap-3"
               data-testid="dm-warning">
            <AlertTriangle size={17} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 leading-relaxed">
              <p className="font-medium">{t("dm.warnTitle", { name: investorName })}</p>
              <p className="mt-1">
                {t("dm.warnBody", { days: state.grant_days, ttl: state.request_ttl_days })}
              </p>
              <p className="mt-1 text-amber-800/80">
                {t("dm.warnRefund")}
              </p>
            </div>
          </div>

          <label className="block">
            <span className="label block mb-1.5">{t("dm.openingLabel")}</span>
            <textarea
              data-testid="dm-opening"
              rows={5}
              className="input"
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t("dm.openingPlaceholder")}
            />
            <span className="block mt-1.5 text-xs text-slate-500 leading-relaxed">
              {t("dm.openingHint")}
            </span>
          </label>

          <label className="flex items-start gap-2.5 text-sm cursor-pointer">
            <input type="checkbox" data-testid="dm-confirm" className="mt-0.5"
                   checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span className="text-slate-600 leading-relaxed">
              {t("dm.confirmLabel", { name: investorName })}
            </span>
          </label>

          <button data-testid="dm-send" className="btn-primary w-full justify-center"
                  onClick={send} disabled={!canSend}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {t("dm.sendRequest")}
          </button>
          <p className="text-[11px] text-slate-400 text-center">
            {t("dm.requestsLeft", {
              left: Math.max(0, (state.requests_allowed || 0) - (state.requests_used || 0)),
            })}
          </p>
        </div>
      )}
    </Modal>
  );
}
